import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * Drop-in replacement for `<Input type="password" />` used app-wide.
 * Eye toggle + uniform min-length helper (7 chars, no other rules).
 *
 * Why a shared component instead of inline toggles?
 *   1. UX consistency — the same eye position + a11y behaviour everywhere.
 *   2. Enforces the same length rule across login, register, set-password,
 *      forgot-password and change-password forms with one source of truth.
 */
export const PASSWORD_MIN_LENGTH = 7;

/** Validate against the uniform rule. Returns null if ok, else an error string. */
export function validatePassword(value) {
  if (!value || value.length < PASSWORD_MIN_LENGTH) {
    return `Use at least ${PASSWORD_MIN_LENGTH} characters.`;
  }
  return null;
}

export default function PasswordInput({
  value,
  onChange,
  placeholder = "Password",
  required = true,
  autoFocus = false,
  testId,
  className = "",
  autoComplete = "current-password",
  variant = "default", // "default" (border-2) | "underline" (border-b-2, used in Auth.jsx)
}) {
  const [visible, setVisible] = useState(false);
  const baseCls = variant === "underline"
    ? "w-full px-0 py-2 pr-9 border-0 border-b-2 border-neutral-300 focus:border-black focus:outline-none rounded-none bg-transparent text-base"
    : "w-full px-3 py-2 pr-10 border-2 border-neutral-300 focus:border-black focus:outline-none rounded-none text-sm";
  return (
    <div className={`relative ${className}`}>
      <input
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        data-testid={testId}
        className={baseCls}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        data-testid={testId ? `${testId}-eye` : undefined}
        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-black"
        tabIndex={-1}
      >
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}
